/**
 * Deep link handler
 * Manages both "Reading" (reacting to hash changes) and "Writing" (updating hash).
 */

const handlers = [];
const queryParamHandlers = [];
let listening = false;
let queryListening = false;

/**
 * Register a selector and handler for deep linking.
 * @param {string} selector - CSS selector for the component's content.
 * @param {function} handler - Function to handle activation.
 * @returns {void}
 */
export function register(selector, handler) {
	handlers.push({ selector, handler });

	if (!listening) {
		listening = true;
		window.addEventListener('hashchange', handleHash);
	}
}

/**
 * Register a selector and handler for deep linking.
 * @param {string} param - The query parameter to listen for.
 * @param {string} selector - CSS selector for the component's content.
 * @param {function} handler - Function to handle activation.
 * @returns {void}
 */
export function registerQueryParam(param, selector, handler) {
	queryParamHandlers.push({ param, selector, handler });

	if (!queryListening) {
		queryListening = true;
		window.addEventListener('popstate', handleQueryParam);
	}
}

/**
 * Remove a previously registered handler.
 * @param {string} selector - The selector used during registration.
 * @param {function} handler - The handler function used during registration.
 */
export function unregister(selector, handler) {
	const index = handlers.findIndex((h) => h.selector === selector && h.handler === handler);

	if (index !== -1) handlers.splice(index, 1);
}

/**
 * Remove a previously registered query parameter handler.
 * @param {string} param - The query parameter used during registration.
 * @param {string} selector - The selector used during registration.
 * @param {function} handler - The handler function used during registration.
 */
export function unregisterQueryParam(param, selector, handler) {
	const index = queryParamHandlers.findIndex((h) => h.param === param && h.selector === selector && h.handler === handler);

	if (index !== -1) queryParamHandlers.splice(index, 1);
}

/**
 * Updates or clears the URL hash without scrolling.
 * Used by components to sync open/close state with the URL.
 * @param {string} id - The element ID.
 * @param {boolean} set - True to set hash, False to clear it.
 * @returns {void}
 */
export function updateHash(id, set) {
	if (!id) return;

	if (set) {
		history.replaceState(null, '', `#${id}`);
	} else if (window.location.hash === `#${id}`) {
		history.replaceState(null, '', window.location.pathname + window.location.search);
	}
}

/**
 * Updates or clears a query parameter
 * Used by components to sync open/close state with the URL.
 * @param {string} id - The element ID.
 * @param {boolean} set - True to set hash, False to clear it.
 * @returns {void}
 */
export function updateQueryParam(param, value, set) {
	if (!param) return;

	const currentUrl = new URL(window.location.href);
	const params = currentUrl.searchParams;

	if (set) {
		params.set(param, value);
	} else {
		params.delete(param);
	}

	history.replaceState(null, '', `${currentUrl.pathname}?${params.toString()}${currentUrl.hash ? currentUrl.hash : ''}`);
}

/**
 * Checks the current hash and calls the first matching handler.
 * @returns {void}
 */
export function handleHash() {
	let hash;

	try {
		hash = decodeURIComponent(window.location.hash);
	} catch {
		return;
	}

	if (!hash || hash.length < 2) return;

	const id = hash.slice(1);
	const targetEl = document.getElementById(id);

	if (!targetEl) return;

	for (const { selector, handler } of handlers) {
		const match = targetEl.closest(selector);

		if (match) {
			handler(match);
			break;
		}
	}
}

/**
 * Checks the current query parameters and calls the first matching handler.
 * @returns {void}
 */
export function handleQueryParam() {
	let params;

	try {
		params = Array.from(new URLSearchParams(window.location.search).entries());
	} catch {
		return;
	}

	if (!params) return;

	params.forEach((p) => {
		for (const { param, selector, handler } of queryParamHandlers) {
			if (param !== p[0]) continue;
			if (selector !== p[1]) continue;

			handler(document.getElementById(p[1]));
		}
	});
}

export default { register, unregister, updateHash, registerQueryParam, unregisterQueryParam, updateQueryParam };
